import type { ColumnSort } from '@tanstack/react-table';

import type { ArtistTracksSortBy } from '~/modules/artist-roster/lib/types';

export const calculateDeltaRatio = ({
  current,
  previous,
  digits = 1,
}: {
  current: number;
  previous: number;
  digits?: number;
}) => {
  return !current || !previous
    ? 0
    : Number((((current - previous) / previous) * 100).toFixed(digits));
};

export function formatArtistTracksSortBy(
  columnSort?: ColumnSort,
): ArtistTracksSortBy {
  const columnId = columnSort?.id;
  const sortDirection = columnSort?.desc ? '-' : '+';

  if (columnId === 'initialReleaseDate') {
    return `${sortDirection}EARLIEST_RELEASE_DATE`;
  }

  if (columnId === 'title') {
    return `${sortDirection}GROUPING_TITLE`;
  }

  return '';
}

const UNITS = ['', 'K', 'M', 'B', 'T'];

/**
 * This formats the follower count by rounding at the first decimal point
 * if the number is > 1000. We support up to 999.9B and that's more than enough
 * for now as there is no one on any social platform that currently even comes
 * clost to 1B followers:
 *  - https://en.wikipedia.org/wiki/List_of_most-followed_Instagram_accounts
 *  - https://en.wikipedia.org/wiki/List_of_most-followed_TikTok_accounts
 *  - https://en.wikipedia.org/wiki/List_of_most-subscribed_YouTube_channels
 *  - https://chartmasters.org/spotify-most-followed-artists/
 *
 * @param count The follower count.
 * @returns The formatted follower count.
 */
export function formatCount(count?: number | null) {
  // Can show either the actual count (including 0) or '--' if the count is null.
  // https://www.figma.com/design/Zw3MrTjcjB9hQUsGRwfQJz?node-id=1792-61696#1134212300
  if (count === undefined || count === null) {
    return '--';
  }

  if (count < 1000) {
    return `${count}`;
  }

  let magnitude = 0;
  let scaled = count;

  while (scaled >= 1000 && magnitude < UNITS.length - 1) {
    scaled /= 1000;
    magnitude++;
  }

  let rounded = Math.round(scaled * 10) / 10;

  // If rounding bumps up the unit (e.g., 999.95K -> 1000K), increase magnitude
  if (rounded >= 1000 && magnitude < UNITS.length - 1) {
    rounded = rounded / 1000;
    magnitude++;
  }

  return `${rounded}${UNITS[magnitude]}`;
}
